import type { Ref } from "vue"
import { computed } from "vue"

/**
 * Builds the ` (Berlin, Hamburg)` fragment used in the job detail title and
 * description.
 *
 * `job.location` carries one city entry per job address, so a job posted for
 * 13 stores in the same city arrives as `["Hamburg", "Hamburg", …]` and used to
 * render as `(Hamburg, Hamburg, …)` in the tab title and Google snippet
 * (JHR-404). Duplicates are dropped case-insensitively while keeping the first
 * spelling and the original order.
 */
function formatLocations(location: unknown): string {
  if (!Array.isArray(location))
    return ""

  const seen = new Set<string>()
  const locations: string[] = []

  for (const entry of location) {
    if (typeof entry !== "string")
      continue

    const trimmed = entry.trim()
    const key = trimmed.toLocaleLowerCase()

    if (!trimmed || seen.has(key))
      continue

    seen.add(key)
    locations.push(trimmed)
  }

  return locations.length > 0 ? ` (${locations.join(", ")})` : ""
}

export function useJobSeoMeta(job: Ref<FlatJob | null | undefined>) {
  const { t } = useI18n()

  const locationsText = computed(() => formatLocations(job.value?.location))

  const title = computed(() => {
    const currentJob = job.value

    if (!currentJob)
      return ""

    const jobTitle = currentJob.title || ""
    const company = currentJob.company || ""

    if (!jobTitle && !company)
      return ""

    if (!company)
      return `${jobTitle}${locationsText.value}`

    return `${jobTitle}${locationsText.value} - ${company}`
  })

  const description = computed(() => {
    const currentJob = job.value

    if (!currentJob)
      return ""

    return t("seo.job-detail-description", {
      jobTitle: currentJob.title || "",
      locations: locationsText.value,
      company: currentJob.company || "",
    })
  })

  return {
    title,
    description,
  }
}
